import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar as CalendarIcon, Clock, CheckCircle, Target, TrendingUp, BookOpen, Plus, Edit, Trash2, Play, Pause, RotateCcw, Award, Flame } from "lucide-react";
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const localizer = momentLocalizer(moment);

const StudyPlanner = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [goals, setGoals] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  // Pomodoro Timer states
  const [pomodoroTime, setPomodoroTime] = useState(25 * 60); // 25 minutes
  const [pomodoroRunning, setPomodoroRunning] = useState(false);
  const [pomodoroMode, setPomodoroMode] = useState('focus'); // focus, shortBreak, longBreak
  const [pomodoroSessions, setPomodoroSessions] = useState(0);

  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    subject: "",
    priority: "medium",
    due_date: "",
    due_time: "",
    estimated_duration: "",
    tags: []
  });

  const [goalForm, setGoalForm] = useState({
    title: "",
    description: "",
    target_date: "",
    category: "exam",
    milestones: []
  });

  const [scheduleForm, setScheduleForm] = useState({
    subject: "",
    day_of_week: "monday",
    start_time: "",
    end_time: "",
    notes: ""
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  // Pomodoro Timer Effect
  useEffect(() => {
    let interval = null;
    if (pomodoroRunning && pomodoroTime > 0) {
      interval = setInterval(() => {
        setPomodoroTime(time => time - 1);
      }, 1000);
    } else if (pomodoroTime === 0) {
      handlePomodoroComplete();
    }
    return () => clearInterval(interval);
  }, [pomodoroRunning, pomodoroTime]);

  const fetchAllData = async () => {
    try {
      const [statsRes, tasksRes, goalsRes, schedulesRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/study-stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/study-tasks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/study-goals`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/study-schedules`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/study-sessions`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      setStats(statsRes.data);
      setTasks(tasksRes.data);
      setGoals(goalsRes.data);
      setSchedules(schedulesRes.data);
      setSessions(sessionsRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load study planner data");
    } finally {
      setLoading(false);
    }
  };

  const handlePomodoroComplete = () => {
    setPomodoroRunning(false);
    const audio = new Audio('/notification.mp3');
    audio.play().catch(() => {});
    
    if (pomodoroMode === 'focus') {
      const newSessions = pomodoroSessions + 1;
      setPomodoroSessions(newSessions);
      toast.success('Focus session complete! Time for a break.');
      
      if (newSessions % 4 === 0) {
        setPomodoroMode('longBreak');
        setPomodoroTime(15 * 60);
      } else {
        setPomodoroMode('shortBreak');
        setPomodoroTime(5 * 60);
      }
    } else {
      toast.success('Break complete! Ready for another focus session?');
      setPomodoroMode('focus');
      setPomodoroTime(25 * 60);
    }
  };

  const handleCreateTask = async () => {
    try {
      await axios.post(`${API}/study-tasks`, taskForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task created successfully');
      setShowTaskModal(false);
      setTaskForm({
        title: "", description: "", subject: "", priority: "medium",
        due_date: "", due_time: "", estimated_duration: "", tags: []
      });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await axios.put(`${API}/study-tasks/${taskId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/study-tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleCreateGoal = async () => {
    try {
      await axios.post(`${API}/study-goals`, goalForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Goal created successfully');
      setShowGoalModal(false);
      setGoalForm({ title: "", description: "", target_date: "", category: "exam", milestones: [] });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to create goal');
    }
  };

  const handleCreateSchedule = async () => {
    try {
      await axios.post(`${API}/study-schedules`, scheduleForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Schedule created successfully');
      setShowScheduleModal(false);
      setScheduleForm({ subject: "", day_of_week: "monday", start_time: "", end_time: "", notes: "" });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to create schedule');
    }
  };

  // Calendar events formatting
  const calendarEvents = [
    ...tasks.map(task => ({
      title: task.title,
      start: new Date(task.due_date),
      end: new Date(task.due_date),
      resource: { type: 'task', data: task }
    })),
    ...goals.map(goal => ({
      title: goal.title,
      start: new Date(goal.target_date),
      end: new Date(goal.target_date),
      resource: { type: 'goal', data: goal }
    }))
  ];

  // Analytics data preparation
  const weeklyStudyData = sessions.slice(0, 7).reverse().map((session, index) => ({
    day: moment(session.date).format('ddd'),
    hours: parseFloat(session.duration_hours || 0)
  }));

  const subjectData = [
    { name: 'Mathematics', hours: 12, fill: '#8884d8' },
    { name: 'Science', hours: 10, fill: '#82ca9d' },
    { name: 'English', hours: 8, fill: '#ffc658' },
    { name: 'History', hours: 6, fill: '#ff8042' },
  ];

  const formatPomodoroTime = () => {
    const minutes = Math.floor(pomodoroTime / 60);
    const seconds = pomodoroTime % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[priority] || colors.medium;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading Study Planner...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Study Planner</h1>
              <p className="text-gray-600 text-sm md:text-base">Plan, track, and achieve your study goals</p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Mobile: Horizontal scrollable tabs */}
          <div className="sm:hidden overflow-x-auto -mx-4 px-4 mb-8">
            <TabsList className="inline-flex w-auto min-w-full bg-muted/30 p-2 gap-2">
              <TabsTrigger 
                value="dashboard" 
                className="flex-shrink-0 min-h-[44px] min-w-[110px] px-4 py-2 flex items-center justify-center whitespace-nowrap data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Target className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Dashboard</span>
              </TabsTrigger>
              <TabsTrigger 
                value="tasks" 
                className="flex-shrink-0 min-h-[44px] min-w-[110px] px-4 py-2 flex items-center justify-center whitespace-nowrap data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Tasks</span>
              </TabsTrigger>
              <TabsTrigger 
                value="calendar" 
                className="flex-shrink-0 min-h-[44px] min-w-[110px] px-4 py-2 flex items-center justify-center whitespace-nowrap data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Calendar</span>
              </TabsTrigger>
              <TabsTrigger 
                value="analytics" 
                className="flex-shrink-0 min-h-[44px] min-w-[110px] px-4 py-2 flex items-center justify-center whitespace-nowrap data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Analytics</span>
              </TabsTrigger>
              <TabsTrigger 
                value="pomodoro" 
                className="flex-shrink-0 min-h-[44px] min-w-[110px] px-4 py-2 flex items-center justify-center whitespace-nowrap data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Clock className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Pomodoro</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Desktop: Grid layout */}
          <div className="hidden sm:block">
            <TabsList className="grid w-full grid-cols-5 mb-8 bg-muted/30 p-2 gap-2">
              <TabsTrigger 
                value="dashboard" 
                className="min-h-[44px] flex items-center justify-center data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Target className="w-4 h-4 mr-2" />
                <span className="font-medium">Dashboard</span>
              </TabsTrigger>
              <TabsTrigger 
                value="tasks" 
                className="min-h-[44px] flex items-center justify-center data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                <span className="font-medium">Tasks</span>
              </TabsTrigger>
              <TabsTrigger 
                value="calendar" 
                className="min-h-[44px] flex items-center justify-center data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <CalendarIcon className="w-4 h-4 mr-2" />
                <span className="font-medium">Calendar</span>
              </TabsTrigger>
              <TabsTrigger 
                value="analytics" 
                className="min-h-[44px] flex items-center justify-center data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                <span className="font-medium">Analytics</span>
              </TabsTrigger>
              <TabsTrigger 
                value="pomodoro" 
                className="min-h-[44px] flex items-center justify-center data-[state=active]:bg-white data-[state=active]:shadow-md focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                <Clock className="w-4 h-4 mr-2" />
                <span className="font-medium">Pomodoro</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="border-none shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Tasks</p>
                      <p className="text-3xl font-bold">{tasks.length}</p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-primary" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Active Goals</p>
                      <p className="text-3xl font-bold">{goals.filter(g => g.status === 'active').length}</p>
                    </div>
                    <Target className="w-10 h-10 text-success" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Study Hours</p>
                      <p className="text-3xl font-bold">{stats?.total_study_hours || 0}h</p>
                    </div>
                    <Clock className="w-10 h-10 text-warning" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Study Streak</p>
                      <p className="text-3xl font-bold">{stats?.study_streak || 0}</p>
                    </div>
                    <Flame className="w-10 h-10 text-orange-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Today's Tasks */}
            <Card className="border-none shadow-md">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Today's Tasks</CardTitle>
                  <Button onClick={() => setShowTaskModal(true)} size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Task
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {tasks.slice(0, 5).length > 0 ? (
                  <div className="space-y-3">
                    {tasks.slice(0, 5).map(task => (
                      <div key={task.id} className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                        <input
                          type="checkbox"
                          checked={task.status === 'completed'}
                          onChange={() => handleUpdateTask(task.id, { status: task.status === 'completed' ? 'pending' : 'completed' })}
                          className="w-5 h-5 rounded"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold">{task.title}</h4>
                          <p className="text-sm text-muted-foreground">{task.subject}</p>
                        </div>
                        <Badge variant="outline" className={getPriorityColor(task.priority)}>
                          {task.priority}
                        </Badge>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteTask(task.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No tasks for today</p>
                )}
              </CardContent>
            </Card>

            {/* Active Goals */}
            <Card className="border-none shadow-md">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Active Goals</CardTitle>
                  <Button onClick={() => setShowGoalModal(true)} size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Goal
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {goals.filter(g => g.status === 'active').length > 0 ? (
                  <div className="space-y-4">
                    {goals.filter(g => g.status === 'active').map(goal => (
                      <div key={goal.id} className="p-4 bg-muted/30 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold">{goal.title}</h4>
                          <Badge variant="outline">{goal.category}</Badge>
                        </div>
                        <Progress value={goal.progress || 0} className="mb-2" />
                        <p className="text-sm text-muted-foreground">{goal.progress || 0}% Complete</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No active goals</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="space-y-6">
            <Card className="border-none shadow-md">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>All Tasks</CardTitle>
                  <Button onClick={() => setShowTaskModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Task
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {tasks.length > 0 ? (
                  <div className="space-y-3">
                    {tasks.map(task => (
                      <div key={task.id} className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                        <input
                          type="checkbox"
                          checked={task.status === 'completed'}
                          onChange={() => handleUpdateTask(task.id, { status: task.status === 'completed' ? 'pending' : 'completed' })}
                          className="w-5 h-5 rounded"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold">{task.title}</h4>
                          <p className="text-sm text-muted-foreground">{task.description}</p>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">{task.subject}</Badge>
                            {task.due_date && (
                              <Badge variant="outline" className="text-xs">
                                <CalendarIcon className="w-3 h-3 mr-1" />
                                {moment(task.due_date).format('MMM DD')}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Badge variant="outline" className={getPriorityColor(task.priority)}>
                          {task.priority}
                        </Badge>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteTask(task.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No tasks yet. Create one to get started!</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <Card className="border-none shadow-md">
              <CardHeader>
                <CardTitle>Study Calendar</CardTitle>
                <CardDescription>View all your tasks and goals in calendar format</CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '600px' }}>
                  <Calendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '100%' }}
                    views={['month', 'week', 'day']}
                    defaultView="month"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Weekly Study Hours */}
              <Card className="border-none shadow-md">
                <CardHeader>
                  <CardTitle>Weekly Study Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={weeklyStudyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="hours" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Subject Distribution */}
              <Card className="border-none shadow-md">
                <CardHeader>
                  <CardTitle>Study Time by Subject</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={subjectData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={entry => entry.name}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="hours"
                      >
                        {subjectData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Task Completion Rate */}
              <Card className="border-none shadow-md lg:col-span-2">
                <CardHeader>
                  <CardTitle>Progress Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium">Task Completion Rate</span>
                        <span className="text-sm text-muted-foreground">
                          {tasks.length > 0 ? Math.round((tasks.filter(t => t.status === 'completed').length / tasks.length) * 100) : 0}%
                        </span>
                      </div>
                      <Progress value={tasks.length > 0 ? (tasks.filter(t => t.status === 'completed').length / tasks.length) * 100 : 0} />
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium">Goal Achievement</span>
                        <span className="text-sm text-muted-foreground">
                          {goals.length > 0 ? Math.round((goals.filter(g => g.status === 'completed').length / goals.length) * 100) : 0}%
                        </span>
                      </div>
                      <Progress value={goals.length > 0 ? (goals.filter(g => g.status === 'completed').length / goals.length) * 100 : 0} className="bg-success" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pomodoro Tab */}
          <TabsContent value="pomodoro">
            <Card className="border-none shadow-md">
              <CardHeader>
                <CardTitle>Pomodoro Timer</CardTitle>
                <CardDescription>Stay focused with the Pomodoro Technique</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center space-y-8 py-8">
                  {/* Timer Display */}
                  <div className="relative">
                    <div className="w-64 h-64 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-2xl">
                      <div className="text-center">
                        <div className="text-6xl font-bold text-white mb-2">{formatPomodoroTime()}</div>
                        <div className="text-white text-sm uppercase tracking-wider">
                          {pomodoroMode === 'focus' ? 'Focus Time' : pomodoroMode === 'shortBreak' ? 'Short Break' : 'Long Break'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Control Buttons */}
                  <div className="flex gap-4">
                    <Button
                      size="lg"
                      onClick={() => setPomodoroRunning(!pomodoroRunning)}
                      className="w-32"
                    >
                      {pomodoroRunning ? (
                        <><Pause className="w-5 h-5 mr-2" /> Pause</>
                      ) : (
                        <><Play className="w-5 h-5 mr-2" /> Start</>
                      )}
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => {
                        setPomodoroRunning(false);
                        setPomodoroTime(pomodoroMode === 'focus' ? 25 * 60 : pomodoroMode === 'shortBreak' ? 5 * 60 : 15 * 60);
                      }}
                      className="w-32"
                    >
                      <RotateCcw className="w-5 h-5 mr-2" /> Reset
                    </Button>
                  </div>

                  {/* Sessions Count */}
                  <div className="text-center">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Award className="w-5 h-5" />
                      <span>Sessions Completed: {pomodoroSessions}</span>
                    </div>
                  </div>

                  {/* Mode Selection */}
                  <div className="flex gap-2">
                    <Button
                      variant={pomodoroMode === 'focus' ? 'default' : 'outline'}
                      onClick={() => {
                        setPomodoroMode('focus');
                        setPomodoroTime(25 * 60);
                        setPomodoroRunning(false);
                      }}
                    >
                      Focus (25m)
                    </Button>
                    <Button
                      variant={pomodoroMode === 'shortBreak' ? 'default' : 'outline'}
                      onClick={() => {
                        setPomodoroMode('shortBreak');
                        setPomodoroTime(5 * 60);
                        setPomodoroRunning(false);
                      }}
                    >
                      Short Break (5m)
                    </Button>
                    <Button
                      variant={pomodoroMode === 'longBreak' ? 'default' : 'outline'}
                      onClick={() => {
                        setPomodoroMode('longBreak');
                        setPomodoroTime(15 * 60);
                        setPomodoroRunning(false);
                      }}
                    >
                      Long Break (15m)
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Task Modal */}
        <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Task</DialogTitle>
              <DialogDescription>Add a new task to your study planner</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Title</Label>
                <Input
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Task title"
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Task description"
                  rows={3}
                />
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  value={taskForm.subject}
                  onChange={(e) => setTaskForm({ ...taskForm, subject: e.target.value })}
                  placeholder="Subject"
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={taskForm.priority} onValueChange={(value) => setTaskForm({ ...taskForm, priority: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Due Time</Label>
                <Input
                  type="time"
                  value={taskForm.due_time}
                  onChange={(e) => setTaskForm({ ...taskForm, due_time: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTaskModal(false)}>Cancel</Button>
              <Button onClick={handleCreateTask}>Create Task</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Goal Modal */}
        <Dialog open={showGoalModal} onOpenChange={setShowGoalModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Goal</DialogTitle>
              <DialogDescription>Set a new study goal to achieve</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2">
                <Label>Title</Label>
                <Input
                  value={goalForm.title}
                  onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                  placeholder="Goal title"
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={goalForm.description}
                  onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                  placeholder="Goal description"
                  rows={3}
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={goalForm.category} onValueChange={(value) => setGoalForm({ ...goalForm, category: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exam">Exam</SelectItem>
                    <SelectItem value="course">Course</SelectItem>
                    <SelectItem value="skill">Skill</SelectItem>
                    <SelectItem value="project">Project</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Target Date</Label>
                <Input
                  type="date"
                  value={goalForm.target_date}
                  onChange={(e) => setGoalForm({ ...goalForm, target_date: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowGoalModal(false)}>Cancel</Button>
              <Button onClick={handleCreateGoal}>Create Goal</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Schedule Modal */}
        <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Study Schedule</DialogTitle>
              <DialogDescription>Add a recurring study schedule</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <Label>Subject</Label>
                <Input
                  value={scheduleForm.subject}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, subject: e.target.value })}
                  placeholder="Subject"
                />
              </div>
              <div>
                <Label>Day of Week</Label>
                <Select value={scheduleForm.day_of_week} onValueChange={(value) => setScheduleForm({ ...scheduleForm, day_of_week: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monday">Monday</SelectItem>
                    <SelectItem value="tuesday">Tuesday</SelectItem>
                    <SelectItem value="wednesday">Wednesday</SelectItem>
                    <SelectItem value="thursday">Thursday</SelectItem>
                    <SelectItem value="friday">Friday</SelectItem>
                    <SelectItem value="saturday">Saturday</SelectItem>
                    <SelectItem value="sunday">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={scheduleForm.start_time}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, start_time: e.target.value })}
                />
              </div>
              <div>
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={scheduleForm.end_time}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, end_time: e.target.value })}
                />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={scheduleForm.notes}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
                  placeholder="Additional notes"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowScheduleModal(false)}>Cancel</Button>
              <Button onClick={handleCreateSchedule}>Create Schedule</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default StudyPlanner;
